export type OrgRowKind = "primary" | "secondary";

/** One tier a firm holds, attributed to the table it came from. */
export type TableRanking = {
  tableName: string | null;
  tier: number | null;
  rankingType: string | null;
  /** WordPress ranking_table term id (number, datamart path) or research ranking_table uuid
   * (string, research path). Null when neither upstream found one. */
  tableId: number | string | null;
};

/** One ranking table's own content figures — see `orgSections.ts`'s `tableContentFor`, its only
 * reader. Keyed by table uuid (or the untabled sentinel) on `contentByTable` below. */
export type TableContent = {
  reviewStatus: string | null;
  hasSubmission: boolean;
  totalFields: number;
  populatedFields: number;
};

/** One organisation as the composed org list endpoint returns it. */
export type OrgListApiRow = {
  companyId: string;
  companyName: string | null;
  officeName: string | null;
  rowKind: OrgRowKind;
  reviewStatus: string | null;
  rankingTier: number | null;
  rankingType: string | null;
  currentRankingTier: number | null;
  currentRankingType: string | null;
  previousRankingTier: number | null;
  previousRankingType: string | null;
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  hasSubmission: boolean;
  populatedFields: number;
  totalFields: number;
  /** Ranking tables this company holds editorial content for — a row can be attributed to a
   * table it has no ranking in yet, which is why section placement checks this too. */
  contentTableIds: string[];
  /** This edition's content figures, one entry per table the company holds a row for (plus the
   * untabled sentinel) — see `orgSections.ts`'s `tableContentFor`, its only reader. */
  contentByTable: Record<string, TableContent>;
};

export type OrgRow = {
  companyUuid: string;
  name: string;
  officeName: string | null;
  rowKind: OrgRowKind;
  reviewStatus: string | null;
  rankingTier: number | null;
  rankingType: string | null;
  currentRankingTier: number | null;
  currentRankingType: string | null;
  previousRankingTier: number | null;
  previousRankingType: string | null;
  // Per-table truth behind the scalars above — one entry per firm table the BPA has.
  currentRankings: TableRanking[];
  previousRankings: TableRanking[];
  hasSubmission: boolean;
  // A "field" is one client, contact or matter entry. The org view recounts the same quantity
  // from the loaded form, so the two screens must agree.
  populatedFields: number;
  totalFields: number;
  // Tables this company holds editorial content for, independent of ranking — see OrgListApiRow.
  contentTableIds: string[];
  // Per-table content figures — see OrgListApiRow.
  contentByTable: Record<string, TableContent>;
};

/**
 * Shown when the row carries a company id but the address-book lookup returned no name.
 * "unavailable" describes the read failure, matching the banner's `Tier unavailable`.
 */
export const UNRESOLVED_FIRM_LABEL = "Firm name unavailable";

/** Keys on the parse signal, not `rowKind`: a row kind of "primary" only says the firm holds an
 * editorial_content row for this edition, which the skeleton import gives nearly every firm. */
export function submissionBadgeLabel(row: { rowKind: OrgRowKind; hasSubmission: boolean }): string {
  return row.hasSubmission ? "Submitted" : "No submission";
}

export function toOrgRows(aggregates: OrgListApiRow[]): OrgRow[] {
  return aggregates.map(aggregate => ({
    companyUuid: aggregate.companyId,
    name: aggregate.companyName ?? UNRESOLVED_FIRM_LABEL,
    officeName:
      aggregate.officeName && aggregate.officeName !== aggregate.companyName
        ? aggregate.officeName
        : null,
    rowKind: aggregate.rowKind,
    reviewStatus: aggregate.reviewStatus,
    rankingTier: aggregate.rankingTier,
    rankingType: aggregate.rankingType,
    currentRankingTier: aggregate.currentRankingTier,
    currentRankingType: aggregate.currentRankingType,
    previousRankingTier: aggregate.previousRankingTier,
    previousRankingType: aggregate.previousRankingType,
    currentRankings: aggregate.currentRankings,
    previousRankings: aggregate.previousRankings,
    hasSubmission: aggregate.hasSubmission,
    populatedFields: aggregate.populatedFields,
    totalFields: aggregate.totalFields,
    contentTableIds: aggregate.contentTableIds,
    contentByTable: aggregate.contentByTable,
  }));
}
